/**
 * Borrower Auth Provider — separate context for borrower portal JWT.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

const BorrowerAuthCtx = createContext(null);
export const useBorrowerAuth = () => useContext(BorrowerAuthCtx);
const API = process.env.REACT_APP_BACKEND_URL;

export default function BorrowerAuthProvider({ children }) {
  const [borrower, setBorrower] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp_borrower')); } catch { return null; }
  });
  const [tenant, setTenant] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp_tenant')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('bp_token'));
  const isAuthenticated = !!token;

  useEffect(() => {
    if (token) localStorage.setItem('bp_token', token);
    else localStorage.removeItem('bp_token');
  }, [token]);

  useEffect(() => {
    if (borrower) localStorage.setItem('bp_borrower', JSON.stringify(borrower));
    else localStorage.removeItem('bp_borrower');
  }, [borrower]);

  useEffect(() => {
    if (tenant) localStorage.setItem('bp_tenant', JSON.stringify(tenant));
    else localStorage.removeItem('bp_tenant');
  }, [tenant]);

  const login = useCallback((data) => {
    setToken(data.access_token);
    setBorrower(data.borrower);
    setTenant(data.tenant);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setBorrower(null);
    setTenant(null);
    localStorage.removeItem('bp_token');
    localStorage.removeItem('bp_borrower');
    localStorage.removeItem('bp_tenant');
  }, []);

  const apiFetch = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Authorization': token ? `Bearer ${token}` : '',
      ...options.headers,
    };
    // Only set Content-Type if not explicitly removed (for multipart uploads)
    if (!('Content-Type' in (options.headers || {}))) {
      headers['Content-Type'] = 'application/json';
    } else if (headers['Content-Type'] === undefined) {
      delete headers['Content-Type'];
    }
    const resp = await fetch(`${API}${endpoint}`, { ...options, headers });
    if (resp.status === 401) {
      logout();
      throw new Error('Session expired');
    }
    return resp;
  }, [token, logout]);

  const apiGet = useCallback(async (endpoint) => {
    const r = await apiFetch(endpoint);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || `API Error: ${r.status}`);
    }
    return r.json();
  }, [apiFetch]);

  const apiPost = useCallback(async (endpoint, data) => {
    const r = await apiFetch(endpoint, { method: 'POST', body: JSON.stringify(data) });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || `API Error: ${r.status}`);
    }
    return r.json();
  }, [apiFetch]);

  const contextValue = useMemo(() => ({
    borrower, tenant, token, isAuthenticated,
    login, logout, apiFetch, apiGet, apiPost,
  }), [borrower, tenant, token, isAuthenticated, login, logout, apiFetch, apiGet, apiPost]);

  return (
    <BorrowerAuthCtx.Provider value={contextValue}>
      {children}
    </BorrowerAuthCtx.Provider>
  );
}

BorrowerAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
